import { Link } from 'react-router'

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/ui/sidebar'
import { useUser } from '@/domains/auth'
import {
  CompanySwitcher,
  useActiveCompanySync,
  useMyCompanies
} from '@/domains/companies'
import { CompanyLogo, useSetupState } from '@/domains/setup'

/** the sidebar's top row. shared between AppShell and OnboardingShell so
 *  the switcher rule (owner of at least one active company → switcher,
 *  else plain link) stays uniform across surfaces. without this
 *  extraction, an owner who created a second company would lose the
 *  switcher the moment the onboarding shell took over the layout. while
 *  the memberships query is loading we render the plain link to avoid
 *  flashing a stale switcher in.
 */
export function SidebarCompanyHeader({
  homeHref = '/'
}: {
  homeHref?: string
}) {
  /** mounts cross-tab + background-tab cookie sync. lives here because
   *  the header ships in both AppShell and OnboardingShell, and is the
   *  one place that's guaranteed to mount as long as the user is in
   *  the app. peer-tab broadcasts and visibilitychange events fire
   *  from any route. */
  useActiveCompanySync()
  const { setupState } = useSetupState()
  const { user } = useUser()
  const { data: companiesData, isLoading: companiesLoading } = useMyCompanies()

  const companies = companiesData?.items ?? []
  const isSuper = user?.isSuper ?? false
  const ownsAnyActiveCompany = companies.some(
    company => company.role === 'owner' && company.status === 'active'
  )
  /** a super always gets the switcher: Super is a row in it, so without one
   *  they could leave Super and have no way back. */
  const canUseSwitcher = !companiesLoading && (isSuper || ownsAnyActiveCompany)

  // the mobile sheet's close button sits over this row's right edge
  return (
    <div className='in-data-[mobile=true]:pr-12'>
      {canUseSwitcher ? (
        <CompanySwitcher
          companies={companies}
          canAddCompany={ownsAnyActiveCompany}
          showSuperScope={isSuper}
        />
      ) : (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size='lg' asChild>
              <Link to={homeHref}>
                <CompanyLogo
                  logoId={setupState?.companyLogoId ?? null}
                  name={setupState?.companyName ?? 'TandemOne'}
                />
                <div className='grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden'>
                  <span className='truncate font-semibold'>
                    {setupState?.companyName ?? 'TandemOne'}
                  </span>
                  <span className='truncate text-xs text-sidebar-foreground/60'>
                    Powered by TandemOne
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      )}
    </div>
  )
}
